import React from "react";
import { useProject } from "../../state/ProjectContext";
import EditableTable from "./_EditableTable";

export default function Testing() {
  const { doc } = useProject();
  const defects = doc?.testing?.defects || [];
  const cycles = doc?.testing?.cycles || [];

  // Defect KPIs
  const open = defects.filter(d => !["Closed", "Deferred"].includes(d.status));
  const bySeverity = { Critical: 0, High: 0, Medium: 0, Low: 0 };
  open.forEach(d => { if (d.severity && bySeverity[d.severity] != null) bySeverity[d.severity]++; });

  return (
    <>
      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 section-eyebrow">Testing Management</div>
        <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-zinc-900 mt-1">Testing</h2>
        <p className="text-sm text-zinc-500 mt-1 max-w-3xl">Unit → SIT → UAT → Regression → Performance cycles with defect burn-down.</p>
      </div>

      {/* Defect severity KPIs */}
      <div className="grid grid-cols-4 gap-3 mb-5" data-export-capture="testing-kpis" data-export-title="Testing · Open defects by severity">
        {[
          { k: "Critical", color: "text-red-700" },
          { k: "High",     color: "text-red-600" },
          { k: "Medium",   color: "text-amber-700" },
          { k: "Low",      color: "text-zinc-700" },
        ].map(({ k, color }) => (
          <div key={k} className="card p-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">{k} defects</div>
            <div className={`font-display text-2xl font-extrabold mt-1 ${color}`}>{bySeverity[k]}</div>
            <div className="text-[10px] text-zinc-500">open</div>
          </div>
        ))}
      </div>

      {/* Cycles */}
      <div className="mb-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-2">Test cycles</div>
        <EditableTable
          sectionKey="testing"
          listKey="cycles"
          testidPrefix="tc"
          labelSingular="cycle"
          columns={[
            { key: "name", label: "Cycle name" },
            { key: "type", label: "Type", type: "select", options: ["Unit", "SIT", "UAT", "Regression", "Performance"], width: "120px" },
            { key: "start", label: "Start", type: "date", width: "140px" },
            { key: "end", label: "End", type: "date", width: "140px" },
            { key: "scripts", label: "Scripts", type: "number", width: "90px" },
            { key: "executed", label: "Executed", type: "number", width: "100px" },
            { key: "passed", label: "Passed", type: "number", width: "90px" },
            { key: "status", label: "Status", type: "select", options: ["Planned", "In progress", "Complete", "Blocked"], width: "120px" },
          ]}
          emptyRow={() => ({ name: "", type: "UAT", start: "", end: "", scripts: 0, executed: 0, passed: 0, status: "Planned" })}
        />
        {cycles.length > 0 && (
          <div className="mt-3 space-y-1.5">
            {cycles.map((c, i) => {
              const p = c.executed ? Math.round((c.passed / c.executed) * 100) : 0;
              return (
                <div key={i} className="grid grid-cols-[220px_1fr_80px] gap-3 items-center text-xs">
                  <div className="truncate">{c.name || `Cycle ${i + 1}`} <span className="text-zinc-400">· {c.type}</span></div>
                  <div className="relative h-3 bg-zinc-100 rounded-sm overflow-hidden">
                    <div className="h-full" style={{ width: `${p}%`, background: p >= 90 ? "#16A34A" : p >= 60 ? "#F59E0B" : "#DC2626" }}/>
                  </div>
                  <div className="text-right font-mono text-[10px] text-zinc-500">{p}% pass</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Defect log */}
      <div className="mb-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-2">Defect log</div>
        <EditableTable
          sectionKey="testing"
          listKey="defects"
          testidPrefix="def"
          labelSingular="defect"
          columns={[
            { key: "ref", label: "Ref", width: "80px" },
            { key: "title", label: "Title" },
            { key: "cycle", label: "Cycle", width: "110px" },
            { key: "severity", label: "Severity", type: "select", options: ["Critical", "High", "Medium", "Low"], width: "110px" },
            { key: "process", label: "Process / requirement", width: "180px" },
            { key: "status", label: "Status", type: "select", options: ["New", "In progress", "Fixed", "Retest", "Closed", "Deferred"], width: "120px" },
            { key: "owner", label: "Owner", width: "120px" },
            { key: "opened", label: "Opened", type: "date", width: "140px" },
          ]}
          emptyRow={() => ({ ref: `DEF-${String(((doc?.testing?.defects || []).length || 0) + 1).padStart(4, "0")}`, title: "", cycle: "UAT", severity: "Medium", process: "", status: "New", owner: "", opened: new Date().toISOString().split("T")[0] })}
        />
      </div>
    </>
  );
}
